"use client";

import { useState } from "react";
import { Switch } from "antd";
import "antd/dist/reset.css"; // Import Ant Design styles
import "./switch-custom.css"; // Import custom switch styles

export default function PreferencesSettings() {
  // Define options as constants to avoid repetition
  const durationOptions = ["All", "15 Min", "30 min", "45 Min", "60 Min"];
  const modeOptions = ["All", "Audio", "Video", "Chat", "In-person"];

  // State for appointment duration selection
  const [appointmentDurations, setAppointmentDurations] = useState([
    "All",
    "15 Min",
    "30 min",
    "45 Min",
    "60 Min",
  ]);

  // State for consultation mode selection
  const [consultationModes, setConsultationModes] = useState([
    "All",
    "Audio",
    "Video",
    "Chat",
    "In-person",
  ]);

  // State for auto-RSVP toggle
  const [autoRSVP, setAutoRSVP] = useState(true);

  // Handle appointment duration selection
  const handleDurationChange = (duration) => {
    setAppointmentDurations((prev) => {
      // If "All" is being checked
      if (duration === "All" && !prev.includes("All")) {
        return durationOptions; // Select all options
      }

      // If "All" is being unchecked
      if (duration === "All" && prev.includes("All")) {
        return []; // Deselect all options
      }

      // If a specific option is being checked
      if (!prev.includes(duration)) {
        const newSelections = [...prev, duration];
        // If all specific options are now selected, also select "All"
        const allSpecificOptionsSelected = durationOptions
          .filter((opt) => opt !== "All")
          .every((opt) => newSelections.includes(opt));

        return allSpecificOptionsSelected
          ? [...newSelections, "All"]
          : newSelections;
      }

      // If a specific option is being unchecked
      const newSelections = prev.filter((item) => item !== duration);
      // If "All" was selected, it should be deselected now
      return newSelections.filter((item) => item !== "All");
    });
  };

  // Handle consultation mode selection
  const handleModeChange = (mode) => {
    setConsultationModes((prev) => {
      // If "All" is being checked
      if (mode === "All" && !prev.includes("All")) {
        return modeOptions; // Select all options
      }

      // If "All" is being unchecked
      if (mode === "All" && prev.includes("All")) {
        return []; // Deselect all options
      }

      // If a specific option is being checked
      if (!prev.includes(mode)) {
        const newSelections = [...prev, mode];
        // If all specific options are now selected, also select "All"
        const allSpecificOptionsSelected = modeOptions
          .filter((opt) => opt !== "All")
          .every((opt) => newSelections.includes(opt));

        return allSpecificOptionsSelected
          ? [...newSelections, "All"]
          : newSelections;
      }

      // If a specific option is being unchecked
      const newSelections = prev.filter((item) => item !== mode);
      // If "All" was selected, it should be deselected now
      return newSelections.filter((item) => item !== "All");
    });
  };

  return (
    <div className="max-w-2xl mx-auto  p-[16px]  bg-white max-h-screen">
      <h1 className="text-[#171717] text-[18px] font-poppins font-semibold ">
        Preferences (Managed by Admin)
      </h1>

      <p className="text-[#171717] text-[14px] font-poppins">
        Adjust your default behavior for sessions and visibility options.
      </p>

      <div className="border-t border-[#E2E8F0] pt-6 pb-4">
        <h2 className="text-[14px] font-poppins font-medium text-[#171717] mb-4">
          Appointment Slot Duration
        </h2>

        <div className="flex flex-wrap gap-4">
          {durationOptions.map((duration) => (
            <label key={duration} className="flex items-center">
              <input
                disabled
                type="checkbox"
                className="h-[24px] w-[24px] rounded-[10px] border-gray-300 text-gray-600 focus:ring-0"
                checked={appointmentDurations.includes(duration)}
                onChange={() => handleDurationChange(duration)}
              />
              <span className="ml-2 text-[14px] font-poppins text-[#171717]">
                {duration}
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="border-t border-gray-200 pt-6 pb-4">
        <h2 className="text-[14px] font-poppins font-medium text-[#171717] mb-4">
          Default Consultation Mode
        </h2>

        <div className="flex flex-wrap gap-4">
          {modeOptions.map((mode) => (
            <label key={mode} className="flex items-center">
              <input
                disabled
                type="checkbox"
                className="h-[24px] w-[24px] rounded-[10px] border-gray-300 text-gray-600 focus:ring-0"
                checked={consultationModes.includes(mode)}
                onChange={() => handleModeChange(mode)}
              />
              <span className="ml-2 text-[14px] font-poppins text-[#171717">
                {mode}
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="border-t border-gray-200 pt-6 pb-4">
        <div className="flex items-center justify-between">
          <span className="text-[14px] font-poppins text-[#171717]">
            Auto-RSVP to Institution Events
          </span>
          <Switch
            disabled
            checked={autoRSVP}
            onChange={setAutoRSVP}
            checkedChildren=""
            unCheckedChildren=""
            className="custom-switch"
          />
        </div>
      </div>
    </div>
  );
}
